import React, { useEffect, useRef, useState } from 'react';
import './in_call_keypad.css';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;
const ECHO_MAX = 16;

type KeypadKey = (typeof KEYS)[number];

const isKeypadKey = (key: string): key is KeypadKey => (KEYS as readonly string[]).includes(key);

type OriginStyle = React.CSSProperties & { '--ick-origin': string };

interface InCallKeypadProps {
  // CSS transform-origin pointing at the toggle icon, e.g. "312px top".
  origin?: string;
  muted?: boolean;
  // Returns true when the call accepted the digit.
  onDigit: (digit: KeypadKey) => boolean;
  onClose: () => void;
}

// Sends one DTMF digit per press; pressed digits stay in local state only and are never tracked.
function InCallKeypad({ origin, muted = false, onDigit, onClose }: InCallKeypadProps) {
  const [sent, setSent] = useState('');
  const displayRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Focus lands on the display so typed digits work without a page-wide key listener.
  useEffect(() => {
    displayRef.current?.focus();
  }, []);

  // Muting disables a focused key, which drops focus to <body>; keep it in the pad instead.
  useEffect(() => {
    if (muted && gridRef.current?.contains(document.activeElement)) displayRef.current?.focus();
  }, [muted]);

  const press = (digit: KeypadKey) => {
    if (onDigit(digit)) setSent((prev) => (prev + digit).slice(-ECHO_MAX));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (!isKeypadKey(e.key) || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
    e.preventDefault();
    // Mute disables the audio track the tones ride on, so a muted press would never reach the callee.
    if (!muted) press(e.key);
  };

  const style: OriginStyle | undefined = origin ? { '--ick-origin': origin } : undefined;

  return (
    <div id="in-call-keypad" className="ick-panel" style={style} role="group" aria-label="Keypad">
      <div
        ref={displayRef}
        className={sent ? 'ick-echo' : 'ick-echo ick-echo-empty'}
        role="textbox"
        aria-readonly="true"
        aria-label="Keys sent"
        tabIndex={0}
        data-track="in-call-keypad-display"
        onKeyDown={handleKeyDown}
      >
        {sent || 'Press a key'}
      </div>
      <div ref={gridRef} className="ick-grid">
        {KEYS.map((digit) => (
          <button
            key={digit}
            type="button"
            className={digit === '*' ? 'ick-key ick-key-star' : 'ick-key'}
            data-track="in-call-keypad-key"
            // Mouse presses leave focus on the display, so no key looks selected afterwards.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => press(digit)}
            disabled={muted}
            onKeyDown={handleKeyDown}
          >
            {digit}
          </button>
        ))}
      </div>
      <p className={muted ? 'ick-hint ick-hint-muted' : 'ick-hint'} aria-live="polite">
        {muted ? 'Unmute to send keys' : <>Sent instantly · can&apos;t be undone</>}
      </p>
    </div>
  );
}

export default InCallKeypad;
